import React, { useState } from 'react';
import { ConfigProvider } from 'antd';
import { useTheme } from '../../theme/ThemeContext';
import GitBranch from '../../atoms/Icon/GitBranch';
import Chevron from '../../atoms/Icon/Chevron';

export interface MethodologySection {
    /** Section title (e.g., "Filters Applied", "Exclusions") */
    title: string;
    /** Array of items in this section */
    items: string[];
}

export interface ViewMethodologyProps {
    /** Title for the methodology section */
    title?: string;
    /** Array of methodology sections */
    sections?: MethodologySection[];
    /** Formula(s) or calculation note(s) to display in the shaded box at the bottom (each item on separate line when array) */
    formula?: string | string[];
    /** Whether the methodology section is initially expanded */
    defaultExpanded?: boolean;
    /** Custom icon to display (defaults to GitBranch) */
    icon?: React.ReactNode;
    /** Callback when expand/collapse state changes */
    onExpandChange?: (expanded: boolean) => void;
    /** Static expanded card for PDF/DOCX capture (no toggle; matches export styling). */
    forExport?: boolean;
    /** Data test attribute */
    'data-test'?: string;
    /** Data test ID attribute */
    'data-test-id'?: string;
}

const ViewMethodology: React.FC<ViewMethodologyProps> = (props) => {
    const {
        title = 'View Methodology',
        sections = [],
        formula,
        defaultExpanded = true,
        icon,
        onExpandChange,
        forExport = false,
    } = props;

    const [isExpanded, setIsExpanded] = useState(defaultExpanded);
    const theme = useTheme();
    const showContent = forExport || isExpanded;

    const handleToggle = () => {
        const newExpanded = !isExpanded;
        setIsExpanded(newExpanded);
        onExpandChange?.(newExpanded);
    };

    const methodologySections = (
        <>
            {sections.length > 0 && (
                <div className="flex items-start justify-between w-full">
                    <div className="flex gap-3 items-start">
                        <div className="size-6 shrink-0" />
                        <div
                            className="font-sans text-[14px] leading-[22px] text-[#1f1f1f]"
                            style={{ fontWeight: 400 }}
                        >
                            {sections.map((section) => (
                                <div key={section.title} className="mb-0">
                                    <p className="m-0" style={{ fontWeight: 600 }}>
                                        {section.title}
                                    </p>
                                    {section.items.map((item) => (
                                        <p
                                            key={`${section.title}-${item}`}
                                            className="m-0"
                                            style={{ fontWeight: 400 }}
                                        >
                                            {'  • '}
                                            {item}
                                        </p>
                                    ))}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {formula &&
                (() => {
                    const formulaLines = Array.isArray(formula) ? formula : [formula];
                    return formulaLines.length > 0 ? (
                        <div className="bg-[#f4f4f5] border border-[#e4e4e7] border-solid flex flex-col items-start px-3 py-2 rounded-lg w-full">
                            {formulaLines.map((line, idx) => (
                                <p
                                    key={idx}
                                    className="font-['Open_Sans'] font-semibold text-[14px] leading-[22px] text-[#4a5565] m-0 w-full"
                                    style={idx > 0 ? { marginTop: '4px' } : undefined}
                                >
                                    {line}
                                </p>
                            ))}
                        </div>
                    ) : null;
                })()}
        </>
    );

    return (
        <ConfigProvider theme={theme}>
            <div
                className="bg-[#fafafa] flex flex-col gap-2.5 items-start px-4 py-3 pb-4 rounded-lg w-full"
                data-test={props['data-test']}
                data-test-id={props['data-test-id']}
                data-view-methodology
            >
                <div className="flex flex-col gap-3 items-start w-full">
                    {forExport ? (
                        <div className="flex items-center gap-3 w-full">
                            {icon || <GitBranch size={24} color="#1f1f1f" />}
                            <p
                                className="font-sans text-[14px] leading-[22px] text-[#1f1f1f] m-0"
                                style={{ fontWeight: 600 }}
                            >
                                {title}
                            </p>
                        </div>
                    ) : (
                        <button
                            type="button"
                            className="flex items-center justify-between w-full cursor-pointer bg-transparent border-none p-0"
                            onClick={handleToggle}
                            aria-expanded={isExpanded}
                            aria-label={isExpanded ? `Collapse ${title}` : `Expand ${title}`}
                        >
                            <div className="flex items-center gap-3">
                                {icon || <GitBranch size={24} color="#1f1f1f" />}
                                <p
                                    className="font-sans text-[14px] leading-[22px] text-[#1f1f1f] m-0"
                                    style={{ fontWeight: isExpanded ? 600 : 400 }}
                                >
                                    {title}
                                </p>
                            </div>
                            <Chevron size={12} color="#71717a" rotate={isExpanded ? 0 : 180} />
                        </button>
                    )}

                    {showContent && <div className="h-[1px] bg-[#e4e4e7] w-full" />}
                </div>

                {showContent && (
                    <>{methodologySections}</>
                )}
            </div>
        </ConfigProvider>
    );
};

export default ViewMethodology;
